import React, { useCallback, useRef } from "react";
import styles from "./image-picker.module.scss";
import { IconButton } from "@src/components/atoms/buttons/icon-button/icon-button";
import imageIcon from "@src/assets/svg/image-icon.svg";
import closeIcon from "@src/assets/svg/close-with-circle-blue-icon.svg";
import {
  ACCEPTED_IMAGE_TYPES_ATTR,
  addImageFiles,
  ImagePickerErrorMessages,
} from "./image-upload-utils";

export type { ImagePickerErrorMessages } from "./image-upload-utils";

interface ImagePickerButtonProps {
  /** Already-selected images — used for the total-count validation. */
  images: File[];
  /** Receives the full merged list (existing + newly accepted files). */
  onAdd: (files: File[]) => void;
  disabled?: boolean;
  pickerLabel: string;
  errorMessages: ImagePickerErrorMessages;
}

/** Adds image files from clipboard paste using the same validation as file upload. */
export const useImagePaste = (
  images: File[],
  onAdd: (files: File[]) => void,
  errorMessages: ImagePickerErrorMessages,
  disabled = false,
  onOtherFiles?: (files: File[]) => boolean,
) => {
  return useCallback(
    (event: React.ClipboardEvent<HTMLInputElement>) => {
      if (disabled || !event.clipboardData) {
        return;
      }

      const clipboardItems = Array.from(event.clipboardData.items ?? []);
      let pastedFiles = clipboardItems
        .filter((item) => item.kind === "file")
        .map((item) => ({ file: item.getAsFile(), itemType: item.type }))
        .filter((item): item is { file: File; itemType: string } =>
          Boolean(item.file),
        );

      // Some browsers expose pasted files through `files` rather than `items`.
      if (pastedFiles.length === 0) {
        pastedFiles = Array.from(event.clipboardData.files ?? []).map(
          (file) => ({ file, itemType: file.type }),
        );
      }

      const imageFiles = pastedFiles
        .filter(
          ({ file, itemType }) =>
            itemType.toLowerCase().startsWith("image/") ||
            file.type.toLowerCase().startsWith("image/"),
        )
        .map(({ file }) => file);
      const otherFiles = pastedFiles
        .filter(
          ({ file, itemType }) =>
            !itemType.toLowerCase().startsWith("image/") &&
            !file.type.toLowerCase().startsWith("image/"),
        )
        .map(({ file }) => file);
      const otherFilesHandled =
        otherFiles.length > 0 && onOtherFiles?.(otherFiles) === true;

      if (imageFiles.length === 0 && !otherFilesHandled) {
        return;
      }

      event.preventDefault();
      if (imageFiles.length > 0) {
        addImageFiles(imageFiles, images, onAdd, errorMessages);
      }
    },
    [disabled, errorMessages, images, onAdd, onOtherFiles],
  );
};

/** Picture-icon button with the hidden file input and validation. Lives in the input row. */
export const ImagePickerButton: React.FC<ImagePickerButtonProps> = ({
  images,
  onAdd,
  disabled = false,
  pickerLabel,
  errorMessages,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFiles = (incoming: File[]) => {
    addImageFiles(incoming, images, onAdd, errorMessages);
  };

  return (
    <>
      <input
        ref={fileInputRef}
        type="file"
        accept={ACCEPTED_IMAGE_TYPES_ATTR}
        multiple
        hidden
        onChange={(e) => {
          handleFiles(Array.from(e.target.files ?? []));
          e.target.value = "";
        }}
      />
      <IconButton
        fun={() => fileInputRef.current?.click()}
        icon={imageIcon}
        disabled={disabled}
        tooltip={pickerLabel}
      />
    </>
  );
};

interface ImagePickerPreviewsProps {
  images: File[];
  onRemove: (index: number) => void;
  removeLabel: string;
  disabled?: boolean;
}

/** Thumbnail strip of the selected images. Lives below the text input field. */
export const ImagePickerPreviews: React.FC<ImagePickerPreviewsProps> = ({
  images,
  onRemove,
  removeLabel,
  disabled = false,
}) => {
  const [previewUrls, setPreviewUrls] = React.useState<string[]>([]);

  React.useEffect(() => {
    const urls = images.map((file) => URL.createObjectURL(file));
    setPreviewUrls(urls);
    return () => urls.forEach((url) => URL.revokeObjectURL(url));
  }, [images]);

  if (previewUrls.length === 0) {
    return null;
  }

  return (
    <div className={styles["image-picker__previews"]}>
      {previewUrls.map((url, index) => (
        <div key={url} className={styles["image-picker__preview"]}>
          <img src={url} alt={images[index]?.name} />
          <button
            type="button"
            className={styles["image-picker__remove"]}
            onClick={() => onRemove(index)}
            disabled={disabled}
          >
            <img src={closeIcon} alt={removeLabel} title={removeLabel} />
          </button>
        </div>
      ))}
    </div>
  );
};
